import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { structureNodeGetLockInfoByEngagementId } from "backend/api/structureNode.service";
import { ConflictOriginatorNodeMapping } from "backend/model/conflictOriginatorNodeMapping";
import { EngagementStructure } from "backend/model/engagementStructure";
import { EngagementTeamMember } from "backend/model/engagementTeamMember";
import { GetLockInfoByEngagementIdQueryRequest } from "backend/model/getLockInfoByEngagementIdQueryRequest";
import { Lock } from "backend/model/lock";
import { ScreenType } from "backend/model/screenType";
import { StructureNode } from "backend/model/structureNode";
import { StructureNodeType } from "backend/model/structureNodeType";
import { APTContext } from "src/app/services/aptContext/apt-context";
import { DocumentTreeNode } from "src/app/workspace/workspace-layout/workspace-document-manager/helpers/DocumentTreeInterface";
import { isNullOrEmpty } from "src/standard/ts/isNullOrEmpty";
import { flattenChilds } from "src/standard/ts/trees";
/**
 * Creates lightweight tree nodes for APTContext without full DocumentTreeNodeData overhead.
 * This avoids loading the heavy DocumentTreeNodeData class and its dependencies.
 */
export function createLightweightTreeNodes(
  nodes: StructureNode[],
  currentUser: EngagementTeamMember | undefined,
): DocumentTreeNode[] {
  return nodes.map((node) => {
    const isDocument =
      node.isContent ||
      node.type === StructureNodeType.StructureDocument ||
      node.type === StructureNodeType.PlanningSummaryReportDocument ||
      node.type === StructureNodeType.StructureManualDocumentObject;

    const isAvailable =
      node.type === StructureNodeType.StructureDocument ||
      node.type === StructureNodeType.PlanningSummaryReportDocument
        ? node.isAvailable
        : true;
    const nodeType =
      node.type === StructureNodeType.PlanningSummaryReportDocument
        ? StructureNodeType.StructureDocument
        : node.type;
    const canHaveChildren =
      nodeType === StructureNodeType.StructureFolder ||
      nodeType === StructureNodeType.StructurePhaseFolder ||
      nodeType === StructureNodeType.StructureRoot ||
      nodeType === StructureNodeType.GeneralAuditAreaFolder ||
      nodeType === StructureNodeType.FinancialStatementAreaFolder;
    const isUploaded =
      (nodeType === StructureNodeType.StructureReport ? true : isAvailable) ||
      !(
        isNullOrEmpty(node.rowVersion) &&
        node.createdById === currentUser?.employeeId &&
        node.isLibrary
      );
    return {
      // eslint-disable-next-line catalyst/localize-message
      label: `${node.index ?? ""} ${node.name ?? ""}`,
      key: node.id,
      data: {
        nodeId: node.id ?? "",
        parentNodeId: node.parentId ?? "",
        sourceId: node.sourceId,
        name: node.name ?? "",
        extension: node.extension?.toLowerCase() ?? "",
        index: node.index ?? "",
        screenType: node.screenType ?? ScreenType.None,
        nodeType: nodeType,
        permissionCode: node.permissionCode,
        isAvailable: isAvailable,
        canDownloadDocument: isDocument,
        isUploaded: isUploaded,
        isDocument: isDocument,
        isFolder: node.isFolder,
        isFolderOrPhase: node.isFolderOrPhase,
        isConflict: false,
        isReport: node.isReport,
        lockDetails: node.locks ?? [],
        sequenceNumber: node.sequenceNumber,
        canHaveChildren: canHaveChildren,
        isItemVisibleInDM: node.isItemVisibleInDM,
        isDeleted: node.isDeleted,
      },
      conflicts: [],
    } as DocumentTreeNode;
  });
}

/**
 * Lightweight service for fetching Table of Contents data.
 * This service is used in critical paths (e.g., route guards) to avoid
 * loading the full DocumentDetailsService in the initial bundle.
 */
@Injectable({ providedIn: "root" })
export class DocumentNavigationService {
  private httpClient = inject(HttpClient);
  private aptContext = inject(APTContext);

  /**
   * Fetches the table of contents for an engagement.
   * This is a minimal version that only fetches the structure data needed for guards.
   * @param engagementId The engagement ID
   * @param isFullRefresh Whether to force a full refresh
   * @returns Promise that resolves when TOC is fetched and processed
   */
  public async getTableofContentAsync(
    engagementId: string,
    isFullRefresh: boolean,
  ): Promise<void> {
    const [engagementStructure, nodesWithConflictedItems, engagementLocks] =
      await Promise.all([
        this.fetchStructureByIdData(engagementId, isFullRefresh),
        this.fetchNodesWithConflictedItemsByEngagementId(
          engagementId,
          isFullRefresh,
        ),
        this.fetchLockInfoByEngagementId({ engagementId }),
      ]);

    // Process the structure into a format APTContext can use
    if (engagementStructure.structureRoot) {
      const flatNodes = flattenChilds([engagementStructure.structureRoot]);
      const lightweightTreeNodes = createLightweightTreeNodes(
        flatNodes,
        this.aptContext.currentUser(),
      );

      // Set the flat DM nodes in APTContext so findScreenNodeByType works
      await this.aptContext.setFlatDMNodes(lightweightTreeNodes);
    }
  }

  private async fetchStructureByIdData(
    engagementID: string,
    isFullRefresh: boolean = false,
  ): Promise<EngagementStructure> {
    const res = await this.aptContext.fetchEngagementStructure(
      engagementID,
      isFullRefresh,
    );
    return res;
  }

  private async fetchNodesWithConflictedItemsByEngagementId(
    engagementID: string,
    isFullRefresh: boolean = false,
  ): Promise<ConflictOriginatorNodeMapping[]> {
    const res = await this.aptContext.fetchNodesWithConflictedItems(
      engagementID,
      isFullRefresh,
    );
    return res;
  }

  private async fetchLockInfoByEngagementId(
    request: GetLockInfoByEngagementIdQueryRequest,
  ): Promise<Lock[]> {
    const res = await structureNodeGetLockInfoByEngagementId(this.httpClient, {
      request,
    });
    const result = await this.aptContext.getAPTResultOrThrow(res);
    return result;
  }
}
